import {
  PropertyTranslator,
  IGenericRequestParametersTranslator,
  IPaginationParametersTranslator,
  IEntryRecordParametersTranslator,
  IModifiedRecordParametersTranslator,
  ISoftDeletableFilterParametersTranslator,
  IEntryRecordLabels,
  ICustomTypedEntityLabels,
  IModificationRecordLabels,
  IPermissionedEntityLabels,
  INamedEntityLabels,
  IRelatedEntityLabels,
  IUniqueEntityLabels,
  ISoftDeletableLabels,
  ITypedEntityParametersTranslator,
  ILockableEntityLabels,
  ILockableEntityParametersTranslator,
} from "./GenericTranslator";
import {
  IRelationModel,
  IGenericRequestParameters,
  IEntityMinimalModel,
  IModelWithIntId,
  INamedEntity,
  IPaginationParameters,
  IPermissionedEntity,
  IRelatedEntity,
  IRelation,
  IUniqueEntity,
  EntityConstants,
  ISoftDeletableFilterParameters,
  ICreatedRecordParameters,
  IModifiedRecordParameters,
  ISoftDeletable,
  IEntryRecord,
  IModificationRecord,
  EntryRecordOrderBy,
  ModificationRecordOrderBy,
  GenericEntityOrderBy,
  ICustomTypedEntity,
  TypedEntitySuggestionsModel,
  ITypedEntityParameters,
  ILockableEntity,
  ILockableEntityParameters,
  TypedEntityOrderBy,
  NamedEntityOrderBy,
} from "./GenericTypes";

export const personsConstants: EntityConstants<Person, PersonFilters> = {
  resource: "persons",
  frontendIndexRoute: "persons",
  entitySingular: "person",
  entityPlural: "persons",
  icon: "user",
};

export const PersonFieldLabels: PropertyTranslator<Person> = {
  ...IPermissionedEntityLabels,
  ...IEntryRecordLabels,
  ...IModificationRecordLabels,
  ...ICustomTypedEntityLabels,
  ...INamedEntityLabels,
  ...IRelatedEntityLabels,
  ...IUniqueEntityLabels,
  ...ISoftDeletableLabels,
  ...ILockableEntityLabels,
  accountState: "Account state",
  email: "E-mail",
  firstName: "First name",
  id: "Person ID",
  isSystemUser: "Is system user",
  lastName: "Last name",
  login: "Login",
  projects: "Project access",
  roles: "Roles",
  has2FA: "Two-factor authentication (2FA)",
  loginType: "Login type",
} as const;

export interface PersonRelations extends IRelationModel {
  datasets: IRelation;
  samples: IRelation;
  labNotebooks: IRelation;
  labNotebookExperiments: IRelation;
  labNotebooksEntries: IRelation;
  labNotebooksEntryMentions: IRelation;
}

type AccountState = "NoAccount" | "Enabled" | "Disabled";

export interface Person
  extends IModelWithIntId,
    IPermissionedEntity,
    IRelatedEntity<PersonRelations>,
    INamedEntity,
    IUniqueEntity,
    ISoftDeletable,
    IEntryRecord,
    IModificationRecord,
    ICustomTypedEntity,
    ILockableEntity {
  firstName?: string;
  lastName: string;
  email?: string | null;
  projects?: IEntityMinimalModel[];
  roles?: IEntityMinimalModel[];
  login?: string;
  accountState?: AccountState;
  isSystemUser?: boolean;
  loginType?: LoginTypes;
  has2FA?: boolean;
}

export type LoginTypes = "SSO" | "LDAP" | "Session";

export const LoginTypesTranslator: Record<LoginTypes, string> = {
  SSO: "Single Sign-On (OIDC)",
  LDAP: "Single Sign-On (LDAP)",
  Session: "Local account",
} as const;

export interface PersonSuggestions extends TypedEntitySuggestionsModel<Person["id"]> {}

export interface PersonWriteModel extends Person {
  password?: string;
  oldPassword?: string;
}
export interface PersonFilters
  extends IGenericRequestParameters<Person, PersonSortingOptions>,
    IPaginationParameters,
    ISoftDeletableFilterParameters,
    ICreatedRecordParameters,
    IModifiedRecordParameters,
    ITypedEntityParameters,
    ILockableEntityParameters {
  roleIds?: number[] | null;
  includeSystemUsers?: boolean;
  hasAccount?: boolean | null;
  isAccountEnabled?: boolean | null;
  logins?: string[] | null;
  loginTypes?: LoginTypes[] | null;
}
export const PersonFiltersTranslator: PropertyTranslator<PersonFilters> = {
  ...IGenericRequestParametersTranslator,
  ...IPaginationParametersTranslator,
  ...ISoftDeletableFilterParametersTranslator,
  ...IEntryRecordParametersTranslator,
  ...IModifiedRecordParametersTranslator,
  ...ITypedEntityParametersTranslator,
  ...ILockableEntityParametersTranslator,
  roleIds: "Role IDs",
  includeSystemUsers: "Include system users",
  hasAccount: "Has account?",
  isAccountEnabled: "Account status",
  logins: "Logins",
  loginTypes: "Login type",
} as const;

export interface multiFormPropertyType {
  label: string;
  value: string;
  isRequired: boolean;
}

export const PersonSortingOptionsConsts = [
  ...GenericEntityOrderBy,
  ...NamedEntityOrderBy,
  ...EntryRecordOrderBy,
  ...ModificationRecordOrderBy,
  ...TypedEntityOrderBy,
  "LOGIN",
  "LOGIN_DISABLED",
  "IS_SYSTEM_USER",
  "LAST_NAME",
  "FIRST_NAME",
] as const;
export type PersonSortingOptions = (typeof PersonSortingOptionsConsts)[number];

export const PersonSearchTermOptions = {
  searchTermIncludeNames: PersonFiltersTranslator["searchTermIncludeNames"],
  searchTermIncludeIds: PersonFiltersTranslator["searchTermIncludeIds"],
  searchTermIncludeUuids: PersonFiltersTranslator["searchTermIncludeUuids"],
  searchTermIncludeCustomFieldValues: PersonFiltersTranslator["searchTermIncludeCustomFieldValues"],
} as const;
